<script>
/**
 * O cartão que aparece quando NENHUM cartão pôde ser montado — spec-001 do
 * `project-plataforma-por-chat`, [T004] / US-2.
 *
 * A regra que ele encarna: dentro de uma conversa, o pior desfecho não é o
 * erro — é o silêncio. Quando o `resolverEntrada` recusa (chave que este front
 * não conhece, módulo não liberado, props fora do contrato), a alternativa a
 * este cartão é um espaço em branco no meio do thread, que o gestor lê como
 * "a IA não respondeu". Aqui ele lê que HAVIA algo ali, por quê não veio, e —
 * quando existe uma tela equivalente — chega nela por um clique.
 *
 * Três decisões pequenas que valem explicar:
 *
 * 1. **Motivo desconhecido aparece cru.** O backend pode estrear um motivo
 *    antes deste front conhecê-lo; mostrar a chave técnica é feio e é
 *    diagnosticável — trocar por "erro" apagaria a única pista.
 * 2. **Ele não oferece atalho para "a tela equivalente"** ([I-2] do review do
 *    PR #2086). Existiam aqui uma prop `rota`, um botão "Abrir na tela" e um
 *    evento `abrir` — sem NINGUÉM que os produzisse (nem `montarFallback`, nem
 *    `montarFalhaDaTool`, nem o `kernel/erro-de-contrato` do backend, cujo
 *    schema declara só `tool` e `erro`) e sem ninguém que os ouvisse (a moldura
 *    do thread liga `respond`, `open-service` e `ask`). Botão que nunca aparece
 *    é código morto que passa por funcionalidade em review. Para ressuscitar são
 *    três peças no MESMO PR: um produtor da rota, `abrir` no contrato de eventos
 *    (`contracts/ui-components.md` §2) e o ouvinte na moldura.
 * 3. **[revertido, 02/09] O ícone voltou, com `fita`.** Entre 31/08 e 02/09
 *    este cartão não tinha ícone — pedido explícito do usuário na época ("um
 *    card de erro genérico não precisa de ícone nenhum... o ícone competia
 *    com o resto da conversa"). Decisão revertida em 02/09, também a pedido
 *    do usuário: junto do redesenho do `ia-confirm-action.vue` (molde de
 *    `open-questions.vue` — faixa de fundo `fita` separando cabeçalho e
 *    corpo por COR), pediu-se o MESMO tratamento aqui, por consistência entre
 *    os cartões de desfecho. `error-circle` em `primary` (mesma cor fixa dos
 *    três estados do `ia-confirm-action`, não mais `await`/âmbar).
 * 4. **Ele não navega, e não decide nada.** É um cartão de leitura: diz o que
 *    houve e a quem pertence o erro. O mesmo cartão serve o thread do chat e
 *    qualquer outra superfície.
 * 5. **O marcador da raiz é `data-cartao`, não `data-teste`** ([I-3] do review
 *    do PR #2086). Dentro do thread, a moldura põe `data-teste="parte-do-turno"`
 *    no MESMO nó, e em Vue 2 o atributo não-prop do pai vence o da raiz do
 *    filho: o `data-teste="fallback-card"` que existia aqui nunca chegava ao
 *    DOM montado pelo renderer — quem procurasse por ele encontrava o vazio e
 *    concluiria que o cartão não montou.
 *
 * [feedback do usuário, 29/08/2026] "Esse componente tá fora do padrão! Tem
 * que ser aquele mesmo de ação confirmada" — a moldura `az-card`/`pa-6` era
 * uma anatomia própria, fora da família `.ia-block-card` que o resto dos
 * cartões de LEITURA já segue (o próprio `_ia-block-card.sass` já previa
 * isso: "o erro do turno" está listado desde a rodada anterior como um dos
 * consumidores do cabeçalho novo, só não tinha sido migrado ainda). Agora
 * segue a MESMA anatomia do `ia-confirm-action.vue` — ícone 14px + `__nome`
 * no cabeçalho, corpo abaixo com a borda da caixa.
 *
 * [feedback do usuário] "Aquela ficou com o texto muito grande" — `__nome`
 * sozinho (20px) é escala de título de SEÇÃO (`cartao-tabela.vue`), pesado
 * demais pra um cartão de status de uma linha só. `__nome--compacto`
 * (`_ia-block-card.sass`) é o modificador que resolve isso pros QUATRO
 * cartões dessa família (este, `ia-confirm-action`, `acao-oferecida-card`, o
 * erro do turno em `chat-message.vue`) — ver o comentário lá pra decisão
 * completa.
 *
 * Sem imports de REGISTRY ou STORE: este é o componente que precisa montar
 * QUANDO o resto falhou, e depender do que pode tê-lo recusado (o registry)
 * ou da store seria construir a peça de contingência sobre o que já quebrou.
 * `CartaoCabecalho` é seguro — presentational puro, zero import próprio.
 */
import CartaoCabecalho from './cartao-cabecalho.vue'

// Os quatro motivos de `MOTIVOS` (ui-registry/index.js), em português de
// gente. Espelho manual — motivo novo lá pede uma linha aqui, senão a chave
// técnica vaza para a tela (o que é feio, mas não é silêncio: ver o computed).
const MOTIVOS_HUMANOS = {
  chave_desconhecida: 'cartão desconhecido',
  flag_desligada: 'módulo não liberado para o escritório',
  schema: 'dados fora do contrato',
  estado_nao_suportado: 'estado não suportado',
}

const TITULO = 'Não consegui montar este cartão'
const SEM_IDENTIFICACAO = 'cartão sem chave'
const SEM_MOTIVO = 'motivo não informado'

export default {
  name: 'FallbackCard',
  components: { CartaoCabecalho },
  props: {
    /** A chave do registry que falhou (`<modulo>/<cartao>`), quando existe. */
    chave: {
      type: String,
      default: '',
    },
    /** Um dos `MOTIVOS` do `ui-registry`. */
    motivo: {
      type: String,
      default: '',
    },
    /** Nome da tool que gerou a parte — identifica quando não há chave. */
    tool: {
      type: String,
      default: '',
    },
    /**
     * O motivo na voz do SERVIDOR (`kernel/erro-de-contrato`, spec-001 [T017]).
     *
     * Quando o `ui` de uma tool reprova no contrato, quem monta o cartão é o
     * backend (`core/ui_payload._cartao_de_erro`), e ele manda `{tool, erro}` —
     * não `motivo`. Sem esta prop, o mesmo cartão que existe para dizer QUAL
     * campo reprovou diria "motivo não informado".
     */
    erro: {
      type: String,
      default: '',
    },
    /**
     * O título do cartão. O default é o de sempre ("não consegui montar").
     *
     * Existe por causa do G-4 da VERIFY it.2: a MESMA peça de contingência
     * passou a servir a tool que FALHOU, e ali não havia cartão para montar —
     * o título de sempre contaria uma história errada sobre o que aconteceu.
     */
    titulo: {
      type: String,
      default: TITULO,
    },
  },
  computed: {
    /**
     * O identificador técnico — chave do registry ou nome da tool.
     *
     * [feedback do usuário, 30/08] "Separar o nome da tool do texto" — antes
     * `chave`/`tool` e o motivo humanizado saíam concatenados numa string só
     * (`detalhe`), sem nenhuma diferença visual entre o identificador cru
     * (`estado_cliente_fee_fixo`) e a frase de verdade — lia como um bloco só
     * de texto técnico. Separado em dois computeds pra o template estilizar
     * cada um: a identificação vira um chip discreto (fonte mono, fundo
     * suave — ver o `<style>`), sem perder a pista de diagnóstico que o
     * motivo 1 do cabeçalho já explica que precisa continuar existindo.
     */
    identificacao() {
      return this.chave || this.tool || SEM_IDENTIFICACAO
    },
    // Ordem: o motivo do FRONT (que tem tradução) manda; sem ele, o texto que o
    // servidor mandou; sem nenhum dos dois, o aviso honesto de que não sabemos.
    // Os dois nunca chegam juntos — quem monta é o front OU o backend.
    motivoHumano() {
      if (this.motivo) return MOTIVOS_HUMANOS[this.motivo] || this.motivo
      if (this.erro) return this.erro
      return SEM_MOTIVO
    },
  },
}
</script>

<template>
  <div
    class="fallback-card ia-block-card"
    data-cartao="fallback"
    role="group"
    :aria-label="titulo"
  >
    <CartaoCabecalho
      :titulo="titulo"
      icone="error-circle"
      cor-do-icone="primary"
      fita
      compacto
    />

    <div class="ia-block-card__corpo">
      <p class="fallback-card__detalhe az-text-primary az-size-12 az-muted mb-0" data-teste="detalhe"><span class="fallback-card__identificacao ia-texto-mono">{{ identificacao }}</span> · {{ motivoHumano }}</p>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// [feedback do usuário, 30/08] "Separar o nome da tool do texto" — ver o
// comentário no <script>. Fonte mono + chip discreto marcam a identificação
// como o que ela é (um dado técnico), sem disputar leitura com a frase.
.fallback-card__identificacao
  color: var(--neutral-700)
  padding: 1px 5px
  border-radius: 4px
  background: var(--neutral-100)
</style>
